/**
 * Pure (UI-model <-> wire-model) helpers for the recursive condition tree editor (plan-011 §8.3-C).
 *
 * The wire format (`ConditionNode[]` on `PromotionRequest.conditions`) is a flat array that is
 * *implicitly AND'd* — "root is an implicit AND group". The editor UI, though, always shows one
 * outer group box (combinator toggle + NOT switch) so a merchant can also express "any of these"
 * or "none of these" at the top level; when they do, serialization wraps the whole tree in one
 * explicit node so the array still has exactly the right meaning. The NOT switch on a group
 * header serializes as a wrapping `NOT` node around that group's equivalent AND/OR node (or,
 * for a one-leaf group, directly around that LEAF — the "exact mapping" plan-011 specifies so a
 * NOT-over-LEAF round-trips back to a one-leaf group with the switch on).
 */
import type { ConditionLeafType, ConditionNode, ConditionNodeType } from '../model/types';

let uidCounter = 0;
/** Client-only synthetic id for React keys — stripped implicitly (the BFF ignores it on write
 *  since `ConditionNode.id` is `readOnly` on responses; existing ids from a loaded promotion are
 *  preserved instead of regenerated, see {@link deserializeNode}). */
function uid(): string {
  uidCounter += 1;
  return `tmp-${Date.now()}-${uidCounter}`;
}

export type ConditionCombinator = Extract<ConditionNodeType, 'AND' | 'OR'>;

export interface ConditionTreeLeaf {
  kind: 'leaf';
  uiId: string;
  type: ConditionLeafType;
  params: Record<string, unknown>;
}

export interface ConditionTreeGroup {
  kind: 'group';
  uiId: string;
  combinator: ConditionCombinator;
  not: boolean;
  children: ConditionTreeNode[];
}

export type ConditionTreeNode = ConditionTreeGroup | ConditionTreeLeaf;

/** Max nesting depth for "add group" (root = depth 0); keeps the tree scannable (plan-011 §8.3-C). */
export const MAX_GROUP_DEPTH = 2;

export function defaultParamsForLeafType(type: ConditionLeafType): Record<string, unknown> {
  switch (type) {
    case 'MIN_SUBTOTAL':
      return { amount: 0 };
    case 'MIN_ITEM_QUANTITY':
      return { quantity: 1 };
    case 'PRODUCT_IN_SET':
    case 'CATEGORY_IN_SET':
      return { productSetId: null };
    case 'CUSTOMER_IN_GROUP':
      return { groupCodes: [] };
    case 'CUSTOMER_KIND':
      return { kind: 'REGISTERED' };
    case 'CUSTOMER_BIRTHDAY':
      return { withinDays: 7 };
    case 'ORDER_COUNT_AT_LEAST':
      return { count: 0 };
    case 'CHANNEL_IN':
      return { channelCodes: [] };
    case 'STORE_IN':
      return { storeCodes: [] };
    case 'DELIVERY_ZONE_IN':
      return { zoneCodes: [] };
    case 'SCHEDULE':
      return { weekdays: [1, 2, 3, 4, 5], fromTime: '00:00', toTime: '23:59' };
    case 'PAYMENT_METHOD_IN':
      return { methodCodes: [] };
    case 'DELIVERY_METHOD_IN':
      return { methodCodes: [] };
    case 'COUPON_PRESENT':
      return {};
    default:
      return {};
  }
}

export function createLeaf(type: ConditionLeafType): ConditionTreeLeaf {
  return { kind: 'leaf', uiId: uid(), type, params: defaultParamsForLeafType(type) };
}

export function createGroup(combinator: ConditionCombinator = 'AND'): ConditionTreeGroup {
  return { kind: 'group', uiId: uid(), combinator, not: false, children: [] };
}

/** A brand-new promotion (or "conditions" cleared) starts from an empty implicit-AND root. */
export function createEmptyRoot(): ConditionTreeGroup {
  return createGroup('AND');
}

export function isRootEmpty(root: ConditionTreeGroup): boolean {
  return root.children.length === 0;
}

// ── Wire -> UI (deserialize) ─────────────────────────────────────────────────────────────────

function deserializeNode(node: ConditionNode): ConditionTreeNode {
  if (node.nodeType === 'LEAF') {
    return {
      kind: 'leaf',
      uiId: node.id ?? uid(),
      type: (node.type ?? 'MIN_SUBTOTAL') as ConditionLeafType,
      params: { ...defaultParamsForLeafType((node.type ?? 'MIN_SUBTOTAL') as ConditionLeafType), ...(node.params ?? {}) },
    };
  }
  if (node.nodeType === 'AND' || node.nodeType === 'OR') {
    return {
      kind: 'group',
      uiId: node.id ?? uid(),
      combinator: node.nodeType,
      not: false,
      children: (node.children ?? []).map(deserializeNode),
    };
  }
  // NOT: exact mapping — a composite child renders as that group with the switch on; a single
  // LEAF child renders as a one-leaf group with the switch on.
  const child = node.children?.[0];
  if (!child) {
    return { kind: 'group', uiId: node.id ?? uid(), combinator: 'AND', not: true, children: [] };
  }
  if (child.nodeType === 'LEAF') {
    return {
      kind: 'group',
      uiId: node.id ?? uid(),
      combinator: 'AND',
      not: true,
      children: [deserializeNode(child)],
    };
  }
  const innerCombinator: ConditionCombinator = child.nodeType === 'OR' ? 'OR' : 'AND';
  return {
    kind: 'group',
    uiId: node.id ?? uid(),
    combinator: innerCombinator,
    not: true,
    children: (child.children ?? []).map(deserializeNode),
  };
}

/** Build the editor's root group from a promotion's flat `conditions[]` array. */
export function conditionsToRoot(conditions: ConditionNode[] | undefined | null): ConditionTreeGroup {
  const list = conditions ?? [];
  if (list.length === 0) return createEmptyRoot();
  if (list.length === 1) {
    const only = list[0];
    const deserialized = deserializeNode(only);
    if (deserialized.kind === 'group') return deserialized;
    // A single top-level LEAF (no wrapping node at all) — normalise to a one-leaf AND root.
    return { kind: 'group', uiId: uid(), combinator: 'AND', not: false, children: [deserialized] };
  }
  // Multiple top-level entries — the common, flat "root is an implicit AND group" case.
  return { kind: 'group', uiId: uid(), combinator: 'AND', not: false, children: list.map(deserializeNode) };
}

// ── UI -> Wire (serialize) ───────────────────────────────────────────────────────────────────

function serializeLeaf(leaf: ConditionTreeLeaf): ConditionNode {
  return { nodeType: 'LEAF', type: leaf.type, params: leaf.params };
}

function serializeGroupAsComposite(group: ConditionTreeGroup): ConditionNode {
  return { nodeType: group.combinator, children: group.children.map(serializeNode) };
}

function serializeNode(node: ConditionTreeNode): ConditionNode {
  if (node.kind === 'leaf') return serializeLeaf(node);
  if (!node.not) return serializeGroupAsComposite(node);
  // NOT-toggled group: one-leaf groups wrap the LEAF directly (exact mapping, see file header);
  // everything else wraps the AND/OR composite.
  if (node.children.length === 1 && node.children[0].kind === 'leaf') {
    return { nodeType: 'NOT', children: [serializeLeaf(node.children[0])] };
  }
  return { nodeType: 'NOT', children: [serializeGroupAsComposite(node)] };
}

/** Serialize the editor's root group back to the flat `conditions[]` array the BFF expects. */
export function rootToConditions(root: ConditionTreeGroup): ConditionNode[] {
  if (root.children.length === 0) return [];
  // The common case — plain AND, no top-level negation — maps straight onto the array's own
  // implicit-AND semantics, keeping the JSON as flat/simple as a hand-authored promotion.
  if (root.combinator === 'AND' && !root.not) {
    return root.children.map(serializeNode);
  }
  // Anything else (OR-at-root, or NOT-at-root) needs one explicit wrapping node so the single
  // conditions[] entry still carries that meaning.
  return [serializeNode(root)];
}

// ── Tree editing (immutable helpers keyed by uiId) ──────────────────────────────────────────

function mapChildren(
  node: ConditionTreeGroup,
  targetUiId: string,
  fn: (children: ConditionTreeNode[]) => ConditionTreeNode[],
): ConditionTreeGroup {
  if (node.uiId === targetUiId) {
    return { ...node, children: fn(node.children) };
  }
  return {
    ...node,
    children: node.children.map((child) =>
      child.kind === 'group' ? mapChildren(child, targetUiId, fn) : child,
    ),
  };
}

export function addLeafTo(root: ConditionTreeGroup, groupUiId: string, type: ConditionLeafType): ConditionTreeGroup {
  return mapChildren(root, groupUiId, (children) => [...children, createLeaf(type)]);
}

export function addGroupTo(root: ConditionTreeGroup, groupUiId: string): ConditionTreeGroup {
  return mapChildren(root, groupUiId, (children) => [...children, createGroup('AND')]);
}

export function removeNode(root: ConditionTreeGroup, targetUiId: string): ConditionTreeGroup {
  function recurse(node: ConditionTreeGroup): ConditionTreeGroup {
    return {
      ...node,
      children: node.children
        .filter((child) => child.uiId !== targetUiId)
        .map((child) => (child.kind === 'group' ? recurse(child) : child)),
    };
  }
  return recurse(root);
}

export function updateLeaf(
  root: ConditionTreeGroup,
  leafUiId: string,
  updater: (leaf: ConditionTreeLeaf) => ConditionTreeLeaf,
): ConditionTreeGroup {
  function recurse(node: ConditionTreeGroup): ConditionTreeGroup {
    return {
      ...node,
      children: node.children.map((child) => {
        if (child.kind === 'leaf') return child.uiId === leafUiId ? updater(child) : child;
        return recurse(child);
      }),
    };
  }
  return recurse(root);
}

export function updateGroup(
  root: ConditionTreeGroup,
  groupUiId: string,
  updater: (group: ConditionTreeGroup) => ConditionTreeGroup,
): ConditionTreeGroup {
  function recurse(node: ConditionTreeGroup): ConditionTreeGroup {
    const self = node.uiId === groupUiId ? updater(node) : node;
    return {
      ...self,
      children: self.children.map((child) => (child.kind === 'group' ? recurse(child) : child)),
    };
  }
  return recurse(root);
}

/** Leaf-level validation — used to render inline `helperText` errors and to gate submit. */
export function isLeafValid(leaf: ConditionTreeLeaf): boolean {
  const p = leaf.params;
  switch (leaf.type) {
    case 'MIN_SUBTOTAL':
      return typeof p.amount === 'number' && p.amount > 0;
    case 'MIN_ITEM_QUANTITY':
      return typeof p.quantity === 'number' && p.quantity > 0;
    case 'PRODUCT_IN_SET':
    case 'CATEGORY_IN_SET':
      return typeof p.productSetId === 'string' && p.productSetId.length > 0;
    case 'CUSTOMER_IN_GROUP':
      return Array.isArray(p.groupCodes) && p.groupCodes.length > 0;
    case 'ORDER_COUNT_AT_LEAST':
      return typeof p.count === 'number' && p.count >= 0;
    case 'CHANNEL_IN':
      return Array.isArray(p.channelCodes) && p.channelCodes.length > 0;
    case 'STORE_IN':
      return Array.isArray(p.storeCodes) && p.storeCodes.length > 0;
    case 'DELIVERY_ZONE_IN':
      return Array.isArray(p.zoneCodes) && p.zoneCodes.length > 0;
    case 'PAYMENT_METHOD_IN':
      return Array.isArray(p.methodCodes) && p.methodCodes.length > 0;
    case 'DELIVERY_METHOD_IN':
      return Array.isArray(p.methodCodes) && p.methodCodes.length > 0;
    case 'CUSTOMER_BIRTHDAY':
      return typeof p.withinDays === 'number' && p.withinDays > 0;
    case 'SCHEDULE':
      return Array.isArray(p.weekdays) && p.weekdays.length > 0;
    case 'CUSTOMER_KIND':
    case 'COUPON_PRESENT':
      return true;
    default:
      return true;
  }
}

/** Whole-tree validity (every leaf valid) — used to gate submit (not draft-save). */
export function isTreeValid(root: ConditionTreeGroup): boolean {
  return root.children.every((child) => (child.kind === 'leaf' ? isLeafValid(child) : isTreeValid(child)));
}
